import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { CLOUD_API_URL } from '../config/serverConfig';
import { useQobuzStore } from '../lib/useQobuz';

const SubscriptionContext = createContext(null);

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscription must be used within SubscriptionProvider');
  return ctx;
}

function shouldBlockAccess(data) {
  if (!data) return false;
  const tier = data.subscription_tier;
  if (tier === 'pending_checkout') return true;
  if (tier === 'expired' && !data.had_trial && !data.stripe_customer_id) return true;
  const activeTiers = ['active', 'trial', 'grace', 'beta', 'cancelling'];
  if (activeTiers.includes(tier)) {
    if (tier === 'trial' && data.trial_ends_at) {
      return new Date(data.trial_ends_at).getTime() < Date.now();
    }
    return false;
  }
  return tier === 'expired' || (tier === 'none' && data.had_trial);
}

export default function SubscriptionProvider({ children }) {
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [previewMode, setPreviewMode] = useState(null);
  const [stripeSlideout, setStripeSlideout] = useState({ open: false, clientSecret: null });
  const [showSubscribedWelcome, setShowSubscribedWelcome] = useState(false);
  const [previewBannerDismissed, setPreviewBannerDismissed] = useState(false);
  const [previewFlash, setPreviewFlash] = useState(false);
  const [trialExpired, setTrialExpired] = useState(false);
  const didAutoFetch = useRef(false);

  const fetchSubscriptionStatus = useCallback(async () => {
    const token = (() => { try { const s = localStorage.getItem('maestro_auth'); return s ? JSON.parse(s).accessToken : null; } catch { return null; } })();
    console.log('[SubStatus] token present:', !!token);
    if (!token) return;
    try {
      const res = await fetch(`${CLOUD_API_URL}/api/stripe/subscription-status`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'omit',
      });
      console.log('[SubStatus] response status:', res.status);
      if (res.ok) {
        const data = await res.json();
        console.log('[SubStatus] data:', JSON.stringify(data));
        console.log('[SubStatus] shouldBlock:', shouldBlockAccess(data));
        setSubscriptionData(data);
        setTrialExpired(shouldBlockAccess(data));
      }
    } catch (e) { console.error('[SubStatus] fetch error:', e); }
  }, []);

  const openStripeCheckout = useCallback(async (cadence = 'monthly') => {
    const token = (() => { try { const s = localStorage.getItem('maestro_auth'); return s ? JSON.parse(s).accessToken : null; } catch { return null; } })();
    if (!token) {
      const { showImperativeToast } = await import('./UIProvider');
      showImperativeToast('Please sign in to manage your subscription.', 5000);
      return false;
    }
    try {
      const r = await fetch(`${CLOUD_API_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify({ ui_mode: 'embedded', cadence }),
      });
      const d = await r.json().catch(() => ({}));
      if (d.clientSecret) {
        setStripeSlideout({ open: true, clientSecret: d.clientSecret });
        return true;
      } else {
        const { showImperativeToast } = await import('./UIProvider');
        const msg = d.error || d.message || (r.status === 409 ? 'You already have an active subscription. Check Settings → Subscription to manage it.' : 'Unable to start checkout. Please try again or contact support.');
        showImperativeToast(msg, 6000);
        return false;
      }
    } catch (e) {
      console.error('[Stripe]', e);
      const { showImperativeToast } = await import('./UIProvider');
      showImperativeToast('Unable to reach the billing server. Please check your connection and try again.', 6000);
      return false;
    }
  }, []);

  useEffect(() => {
    const token = (() => { try { const s = localStorage.getItem('maestro_auth'); return s ? JSON.parse(s).accessToken : null; } catch { return null; } })();
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${CLOUD_API_URL}/api/user/streaming`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'omit',
        });
        if (!cancelled && res.ok) {
          const data = await res.json();
          setPreviewMode(data?.streaming_mode === 'full' ? 'full' : 'preview');
        }
      } catch {}
      if (!cancelled && useQobuzStore.getState().connected) setPreviewMode('full');
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const unsub = useQobuzStore.subscribe((state) => {
      if (state.connected) setPreviewMode('full');
    });
    return unsub;
  }, []);

  useEffect(() => {
    const handler = () => {
      setPreviewFlash(true);
      setTimeout(() => setPreviewFlash(false), 3000);
    };
    window.addEventListener('maestro:preview-ended', handler);
    return () => window.removeEventListener('maestro:preview-ended', handler);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('subscribed') === 'true') {
      setShowSubscribedWelcome(true);
      setTrialExpired(false);
      fetchSubscriptionStatus();
      const url = new URL(window.location.href);
      url.searchParams.delete('subscribed');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    }
  }, [fetchSubscriptionStatus]);

  // Auto-fetch subscription status on mount when authenticated
  useEffect(() => {
    if (didAutoFetch.current) return;
    const token = (() => { try { const s = localStorage.getItem('maestro_auth'); return s ? JSON.parse(s).accessToken : null; } catch { return null; } })();
    if (!token) return;
    didAutoFetch.current = true;
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  // Periodic refresh for trial users (every 10 min) and on tab re-focus
  useEffect(() => {
    const tier = subscriptionData?.subscription_tier;
    if (!tier || tier === 'expired') return;

    const POLL_MS = 10 * 60 * 1000;
    const interval = setInterval(fetchSubscriptionStatus, POLL_MS);

    const onFocus = () => fetchSubscriptionStatus();
    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [subscriptionData?.subscription_tier, fetchSubscriptionStatus]);

  const value = {
    subscriptionData,
    setSubscriptionData,
    previewMode,
    setPreviewMode,
    stripeSlideout,
    setStripeSlideout,
    showSubscribedWelcome,
    setShowSubscribedWelcome,
    previewBannerDismissed,
    setPreviewBannerDismissed,
    previewFlash,
    trialExpired,
    setTrialExpired,
    openStripeCheckout,
    fetchSubscriptionStatus,
  };

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
}
